import React, {Suspense, lazy, useEffect, useState, Fragment} from "react";
import { CssBaseline, useMediaQuery, useTheme} from '@mui/material';
import './App.scss';
import historyR from "./helpers/history";
import type {BrowserHistory} from "history";
import {BrowserRouterProps, Router, Route, Routes, useLocation} from "react-router";

import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { esES } from '@mui/material/locale';

import {getColor} from "./assets/colors";
import LoadingProgress from "./components/LoadingProgress";
import {useAppSelector} from "./redux/hooks";
import {ToastContainer} from "react-toastify";
import {initGA, trackPageView} from "./helpers/analytics";
import {ThemeContext} from "./Context/ThemeContext";
import {TIPO_AVISO, TIPOPLAN} from "./helpers/CONSTANTES";

const Login = lazy(() => import('./views/cuenta/login'));
const Register = lazy(() => import('./views/cuenta/register'));
const ForgotPassword = lazy(() => import('./views/cuenta/ForgotPassword'));
const Solicitar = lazy(() => import('./views/solicitud/Solicitar'));
const EstadoCliente = lazy(() => import('./views/EstadoCliente'));
const Reports = lazy(() => import('./views/reports'));
const WebNotAllow = lazy(() => import('./views/WebNotAllow'));


const Dashboard = lazy(() => import('./layouts/layout'));


export default function App(){
  const auth = useAppSelector(state => state.auth)

  const {uiColor} = auth.user || {uiColor:"#17a2b8"};
  const smVewport = useMediaQuery(useTheme().breakpoints.down('sm'));

  const [isDark, setIsDark] = useState(() => localStorage.getItem('darkMode') === 'true');
  const toggleTheme = () => {
    setIsDark(prev => {
      localStorage.setItem('darkMode', String(!prev));
      return !prev;
    });
  };

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDark);
  }, [isDark]);

  //console.log(themeObj)
  // const hist = createBrowserHistory();
  const buildTheme = (dark: boolean) => createTheme({
      components: {
      MuiSelect: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: 'standard',
        },
        styleOverrides:{
          root: {

            "& label":{
              // background: "white",
            }
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          variant: 'standard',
        },
      },
        MuiDialogContent:{
          styleOverrides:{
            root: {
              padding: "16px",
            }
          }
        },

        MuiDialogActions:{
          styleOverrides:{
            root: {
              borderTop: "1px solid " + (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"),
              padding: "8px 16px",
              "& button:not(.MuiIconButton-root)":{
                padding: "4px 11px",
              }
            }
          }
        },
        MuiOutlinedInput:{
          styleOverrides:{
            input:{
              padding: "11px 15px",
            }
          }
        },

      ...(!smVewport? {
        MuiInputLabel:{
          styleOverrides:{
            root: {
              "&:not([class*='-outlined'])":{
                lineHeight: 1,
                position: "initial !important",
                transform: "none !important",
                color: "inherit !important",
                marginBottom: "7px !important",
                fontSize: "0.95rem",
              }
            },
          }
        },
        // MuiOutlinedInput:{
        //   styleOverrides:{
        //     input:{
        //       padding: "9px 15px",
        //     }
        //   }
        // },

        MuiInputBase:{
          styleOverrides:{
            root:{
              background: dark ? undefined : "white",
              // "& .MuiSelect-select":{
              //   padding: "9px 15px",
              // },

              "&:not([class*='MuiOutlinedInput'])":{
                border: "1px solid " + (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"),
                marginTop: "0px !important",
                "& [class*='-input']:not([class*='Pagination'])":{ //:not(select)
                  padding: "6px 15px",
                },


                "&:before, &:after":{
                  display:"none !important",
                }
              }
            },
            adornedStart:{
              // paddingLeft:"5px !important",
            }
          }
        },
        MuiInputAdornment:{
          styleOverrides:{
              root:{
                  marginRight: 0
              },
            positionStart:{
              "&>p":{
                background: dark ? "rgba(255,255,255,0.16)" : "#ededed",
                height: 35,
                width: 35,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",

              },
              "&.MuiInputAdornment-sizeSmall":{
                "&>p":{
                  height: 31,
                  width: 31,
                }
              }
            }
          }
        }
      }:{}),
      MuiButton: {
        styleOverrides:{
          root: {
            borderRadius: 2,
            // lineHeight:1,

          },
          startIcon:{
            marginBottom: 1,
          }
        }
      },
      MuiTableRow: {
        styleOverrides:{
          root: {
            '&:nth-of-type(odd):not(.MuiTableRow-head)': {
              backgroundColor: dark ? 'transparent' : '#fafafa',
            },
            '&:nth-of-type(even):not(.MuiTableRow-head)': {
              backgroundColor: dark ? 'rgba(255,255,255,0.06)' : '#fdfdfd',
            },
            '&.MuiTableRow-hover:hover': {
              backgroundColor: (dark ? 'rgba(255,255,255,0.12)' : '#f2f2f2') + ' !important',
            },
          },
          head: {
            height: 34,

          },
          footer:{
            cursor: "auto !important",
          }
        }
      },
      MuiTableCell: {
        styleOverrides:{
          root:{
            "&:not([class*='sizeSmall'])":{
              height: 44,
              padding: ".5rem",
              boxSizing: "border-box",
            },
          },
          head: {
            color: uiColor,
            borderBottom: uiColor + " 2px solid",
            background: dark ? "#414141" : "#eeeeee",
          },
          body: {
          },
          footer:{
            fontSize: "0.85rem"
          }
        }
      },
    },
      palette: {
        mode: dark ? 'dark' : 'light',
        ...getColor(uiColor),
        contrastThreshold: 3,
        tonalOffset: 0.2,
      },
      mixins: {
        toolbar: {
          minHeight: 50,
        }
      },
    }, esES);

  const theme = buildTheme(isDark);
  const lightTheme = buildTheme(false);

  const fechaRegistro = auth.user?.fechaCreacion;
  const isOlderThanOneMonth = !!fechaRegistro &&
      (Date.now() - new Date(fechaRegistro).getTime()) > 30 * 24 * 60 * 60 * 1000;
  const isWebBrowser = !(window as any).ReactNativeWebView;
  const isFree = auth.user?.tipoPlan === TIPOPLAN.gratis &&
      isOlderThanOneMonth &&
      auth.user?.tipoAviso !== TIPO_AVISO.temporalDelete &&
      isWebBrowser;



    return (
      (<div className="App">
        <StyledEngineProvider injectFirst>
          <ThemeContext.Provider value={{ toggleTheme, isThemeDark: isDark }}>
          <ThemeProvider  theme={theme}>
            <CssBaseline />
            <Suspense fallback={<LoadingProgress/>}>
              <BrowserRouter basename={"/"}>
                <TrackingPageView>
                    <Routes>
                  <Route path="/login" element={<PublicPage theme={lightTheme}><Login/></PublicPage>}/>
                  <Route path="/register/:noPayment?" element={<PublicPage theme={lightTheme}><Register/></PublicPage>}/>

                  <Route path="/reports/:type/:id?" element={<PublicPage theme={lightTheme}><Reports/></PublicPage>}/>
                  <Route path={"/forgotPasswordConfirm"} element={<PublicPage theme={lightTheme}><ForgotPassword/></PublicPage>}/>
                  <Route path={"/forgotPassword"} element={<PublicPage theme={lightTheme}><ForgotPassword/></PublicPage>}/>


                  <Route path="/solicitarCredito/:user" element={<PublicPage theme={lightTheme}><Solicitar/></PublicPage>}/>
                  <Route path={"/estadoCliente/:user/:prestamoId?"} element={<PublicPage theme={lightTheme}><EstadoCliente/></PublicPage>}/>


                  {auth.isAuthenticated? (
                      // <Suspense fallback={<LoadingProgress/>}>
                      <Fragment>
                          {isFree ?
                              (<Route path={"*"} element={<WebNotAllow/>}/>):
                              (<Route path={"*"} element={<Dashboard />}/>)
                          }


                      </Fragment>

                      // </Suspense>
                  ):(
                      <>
                        <Route path="*" element={auth.status === "loading"? <LoadingProgress/>:<Login/>}/>
                        {/*<Route path="*" element={<Login/>}/>*/}
                      </>
                  )}


                </Routes>
                </TrackingPageView>
              </BrowserRouter>
            </Suspense>
          </ThemeProvider >
          </ThemeContext.Provider>
        </StyledEngineProvider>
        <ToastContainer
            position={smVewport ? 'bottom-center' : 'top-right'}
        />
      </div>)
    );

}

export function BrowserRouter({basename, children}: BrowserRouterProps) {

  let historyRef = React.useRef<BrowserHistory | undefined>(undefined);
  if (historyRef.current == null) {
    historyRef.current = historyR;
  }

  let history = historyRef.current;
  let [state, setState] = React.useState({
    action: history.action,
    location: history.location
  });

  React.useLayoutEffect(() => history.listen(setState), [history]);

  return (
      <Router
          // @ts-ignore
          basename={basename}
          children={children}
          location={state.location}
          navigationType={state.action}
          navigator={history}
      />
  );
}



function TrackingPageView({ children }: React.PropsWithChildren) {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return <>{children}</>;
}

function PublicPage({ theme, children }: { theme: ReturnType<typeof createTheme>, children: React.ReactNode }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="public-page">{children}</div>
    </ThemeProvider>
  );
}
